import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaSpinner } from 'react-icons/fa';
import { useCompare } from '../context/CompareContext';

// --- CURATED LIST ---
// This is our starting list. The code will now
// automatically filter out any of these that have bad data.
const curatedFundLists = {
  'Large Cap': [
    { name: 'Axis Bluechip Fund', code: '120503' },
    { name: 'Canara Robeco Bluechip', code: '118840' },
    { name: 'ICICI Pru Bluechip Fund', code: '100584' },
    { name: 'HDFC Index Fund-NIFTY 50', code: '101925' },
    { name: 'SBI BlueChip Fund', code: '100030' }
  ],
  'Mid Cap': [
    { name: 'Parag Parikh Flexi Cap', code: '122639' },
    { name: 'Axis Midcap Fund', code: '118991' },
    { name: 'Kotak Emerging Equity', code: '118980' },
    { name: 'PGIM India Midcap', code: '120023' },
    { name: 'HDFC Mid-Cap Opportunities', code: '100418' },
  ],
  'Small Cap': [
    { name: 'SBI Small Cap Fund', code: '118836' },
    { name: 'Axis Small Cap Fund', code: '120504' },
    { name: 'Kotak Small Cap Fund', code: '105237' },
    { name: 'Nippon India Small Cap', code: '102132' },
    { name: 'HDFC Small Cap Fund', code: '100466' },
  ],
  'Tax Saver (ELSS)': [
    { name: 'Mirae Asset Tax Saver', code: '130836' }, 
    { name: 'Axis Long Term Equity', code: '112166' },
    { name: 'DSP Tax Saver Fund', code: '102428' },
    { name: 'Canara Robeco Equity Tax Saver', code: '118839' },
  ],
};

const tabs = ['Large Cap', 'Mid Cap', 'Small Cap', 'Tax Saver (ELSS)'];

// --- HELPER FUNCTION ---
// Calculates return based on trading days (approx. 252/year)
const calculateReturn = (navs, years) => {
  const tradingDays = years * 252;
  // Safety check: ensure navs is an array and has enough data
  if (!navs || !Array.isArray(navs) || navs.length < tradingDays) return null;
  
  const latestNav = parseFloat(navs[0].nav);
  const pastNav = parseFloat(navs[tradingDays - 1].nav); // -1 for array index
  
  if (isNaN(latestNav) || isNaN(pastNav) || pastNav === 0) return null;
  
  const returnPercent = ((latestNav - pastNav) / pastNav) * 100;
  return parseFloat(returnPercent.toFixed(2));
};

// Return Cell Component for styling
const ReturnCell = ({ value }) => {
  if (value === null || isNaN(value)) {
    return <span className="text-gray-400">N/A</span>;
  }
  const isPositive = value >= 0;
  return (
    <span className={`font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
      {value}%
    </span>
  );
};


function CategoryPerformers() {
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [fundData, setFundData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState('return1Y'); // Default sort
  const { addFundToCompare } = useCompare();

  useEffect(() => {
    const fetchCategoryData = async () => {
      setIsLoading(true);
      setFundData([]);
      const fundsToFetch = curatedFundLists[activeTab];

      const promises = fundsToFetch.map(fund =>
        fetch(`https://api.mfapi.in/mf/${fund.code}`)
          .then(res => {
            if (!res.ok) throw new Error(`API failed for ${fund.code}`);
            return res.json();
          })
          .then(data => ({
            ...fund,
            return1Y: calculateReturn(data.data, 1),
            return3Y: calculateReturn(data.data, 3),
            return5Y: calculateReturn(data.data, 5),
          }))
          .catch(err => {
            console.error(`Failed to fetch ${fund.name}`, err);
            return null;
          })
      );

      const results = await Promise.all(promises);
      
      // --- THIS IS THE NEW FIX ---
      // 1. Filter out any fund that failed OR does not have a 1-Year return.
      const validResults = results.filter(fund => fund && fund.return1Y !== null);
      
      setFundData(validResults);
      setIsLoading(false);
    };

    fetchCategoryData();
  }, [activeTab]); // Re-run when tab changes

  // --- Memoized sorted data ---
  const sortedFundData = useMemo(() => {
    return [...fundData].sort((a, b) => {
      // Handle nulls by pushing them to the bottom
      const valA = a[sortColumn] === null ? -Infinity : a[sortColumn];
      const valB = b[sortColumn] === null ? -Infinity : b[sortColumn];
      return valB - valA; // Sort descending
    });
  }, [fundData, sortColumn]);

  // --- Sortable Header Cell ---
  const SortableHeader = ({ label, column }) => (
    <button
      onClick={() => setSortColumn(column)}
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${sortColumn === column ? 'text-blue-600' : ''}`}
    >
      {label} {sortColumn === column && '▼'}
    </button>
  );

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">Top Performers by Category</h2>
      
      {/* --- Tabs --- */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex flex-wrap" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`
                ${activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm sm:text-base sm:px-4
              `}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* --- UPGRADED Results Table --- */}
      <div className="flow-root overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <FaSpinner className="animate-spin text-blue-600 h-8 w-8" />
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheme Name</th>
                <th className="hidden md:table-cell"><SortableHeader label="1-Year" column="return1Y" /></th>
                <th className="hidden md:table-cell"><SortableHeader label="3-Year" column="return3Y" /></th>
                <th className="hidden md:table-cell"><SortableHeader label="5-Year" column="return5Y" /></th>
                {/* Mobile view: Show only the active sorted column */}
                <th className="md:hidden"><SortableHeader label="Return" column={sortColumn} /></th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Compare</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedFundData.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan="6" className="text-center text-gray-500 py-12">
                    No funds with complete 1-Year data found for this category.
                  </td>
                </tr>
              ) : (
                sortedFundData.map((fund) => (
                  <tr key={fund.code}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <Link to={`/mutual-funds/${fund.code}`} className="text-sm font-semibold text-blue-600 hover:text-blue-800">
                        {fund.name}
                      </Link>
                    </td>
                    {/* Desktop Columns */}
                    <td className="px-4 py-4 whitespace-nowrap hidden md:table-cell"><ReturnCell value={fund.return1Y} /></td>
                    <td className="px-4 py-4 whitespace-nowrap hidden md:table-cell"><ReturnCell value={fund.return3Y} /></td>
                    <td className="px-4 py-4 whitespace-nowrap hidden md:table-cell"><ReturnCell value={fund.return5Y} /></td>
                    {/* Mobile Column */}
                    <td className="px-4 py-4 whitespace-nowrap md:hidden"><ReturnCell value={fund[sortColumn]} /></td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => addFundToCompare({ schemeCode: fund.code, schemeName: fund.name })}
                        className="py-1 px-3 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
                      >
                        + Compare
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default CategoryPerformers;